import React from 'react';
import { Select } from 'antd';
import { WarehouseOption } from '../../../../hooks/useWarehouseOptions';
import { useTranslation } from 'react-i18next';
import InputFilterItem from '../../../delivery/components/InputFilterItem';
import { useFilterContainerClassName, useFilterInputContainerClassName } from '../../../../utils/languageUtils';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import { DspSelectFilter } from '../../../../components/filters/DspSelectFilter';

export type DspFilterOptions = {
    selectedDspIdsForName?: string[];
    dspCode: string;
    warehouseId: string; // 'all' | warehouseId
    statusFilter: string; // 'all' | '0' | '1'
};

export const DEFAULT_DSP_FILTER_OPTIONS: DspFilterOptions = {
    selectedDspIdsForName: undefined,
    dspCode: '',
    warehouseId: 'all',
    statusFilter: '1'
};

interface FilterAreaProps {
    warehousesOptions: WarehouseOption[];
    loading: boolean;
    filters: DspFilterOptions;
    setFilters: (filters: DspFilterOptions) => void;
    dspFilterHubId?: number;
}

const FilterArea: React.FC<FilterAreaProps> = ({ warehousesOptions, loading, filters, setFilters, dspFilterHubId }) => {
    const { t } = useTranslation();
    const containerClassName = useFilterContainerClassName();
    const inputContainerClassName = useFilterInputContainerClassName();

    // 三列布局：按列最宽对齐
    const column1Labels = [t('central.dsp.filters.name'), t('central.dsp.filters.status')];
    const column2Labels = [t('central.dsp.filters.code')];
    const column3Labels = [t('central.dsp.filters.warehouse')];

    const warehouseIdNum =
        filters.warehouseId !== 'all' ? Number(filters.warehouseId) : undefined;
    const dspSelectWarehouseId = warehouseIdNum;
    const dspSelectHubId =
        filters.warehouseId === 'all' && dspFilterHubId != null ? dspFilterHubId : undefined;

    return (
        <div className="mb-2 py-2 [&_.ant-select]:h-8 [&_.ant-select-selector]:h-8">
            {/* 使用 Grid 布局确保列对齐 */}
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                {/* 第一列 */}
                <div className="min-w-0">
                    <DspSelectFilter
                        label={t('central.dsp.filters.name')}
                        value={filters.selectedDspIdsForName}
                        onChange={(ids) =>
                            setFilters({
                                ...filters,
                                selectedDspIdsForName: ids?.length ? ids.map(String) : undefined,
                            })
                        }
                        spanClassName={getSmartLabelClassName(column1Labels)}
                        placeholder={String(t('components.input.all'))}
                        warehouseId={dspSelectWarehouseId}
                        hubId={dspSelectHubId}
                    />
                </div>
                
                {/* 第二列 */}
                <div className="min-w-0">
                    <InputFilterItem
                        label={t('central.dsp.filters.code')}
                        value={filters.dspCode}
                        onChange={(e) => setFilters({ ...filters, dspCode: e.target.value })}
                        spanClassName={getSmartLabelClassName(column2Labels)}
                    />
                </div>
                
                {/* 第三列 */}
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={getSmartLabelClassName(column3Labels)}>
                            {t('central.dsp.filters.warehouse')}
                        </span>
                        <div className={inputContainerClassName}>
                    <Select
                        loading={loading}
                        placeholder={String(t('components.input.all'))}
                                className="w-full"
                                size="middle"
                        value={filters.warehouseId}
                        onChange={(value) =>
                            setFilters({
                                ...filters,
                                warehouseId: value,
                                selectedDspIdsForName: undefined,
                            })
                        }
                        options={[{
                            value: 'all',
                            label: t('components.input.all')
                        }, ...warehousesOptions.map(w => ({
                            value: w.id,
                            label: w.name
                        }))]
                        }
                    />
                        </div>
                    </div>
                </div>
                {/* 第二行第一列 */}
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={getSmartLabelClassName(column1Labels)}>
                            {t('central.dsp.filters.status')}
                        </span>
                        <div className={inputContainerClassName}>
                            <Select
                                placeholder={String(t('components.input.all'))}
                                className="w-full"
                                size="middle"
                                value={filters.statusFilter}
                                onChange={(value) => setFilters({ ...filters, statusFilter: value })}
                                options={[
                                    { value: 'all', label: t('components.input.all') },
                                    { value: '1', label: t('enum.activeStatus.active') },
                                    { value: '0', label: t('enum.activeStatus.suspended') }
                                ]}
                            />
                        </div>
                    </div>
                </div>
                {/* 第二行第二列和第三列留空 */}
                <div className="min-w-0"></div>
                <div className="min-w-0"></div>
            </div>
        </div>
    );
};

export default FilterArea; 